/**
 * AlarmSound — genera bips de alarma estilo monitor hospitalario con WebAudio.
 *
 * Dos niveles:
 *   - warning (ámbar): bip simple de 0.8kHz, cadencia lenta (1/s)
 *   - critical (rojo): triple bip de 1.2kHz, cadencia rápida (3 bips cada 1.5s)
 *
 * Los navegadores bloquean autoplay hasta que haya un user gesture. Por eso
 * exponemos `prime()`: llamar desde el primer click del alumno para habilitar
 * el AudioContext. Si no se primereó, los bips fallan silenciosamente.
 */

export type AlarmLevel = 'off' | 'warning' | 'critical';

export class AlarmSound {
  private ctx: AudioContext | null = null;
  private currentLevel: AlarmLevel = 'off';
  private timer: number | null = null;
  private muted = false;
  private muteUntil = 0;

  /** Llamar desde un user gesture para habilitar WebAudio. */
  prime(): void {
    if (this.ctx) return;
    try {
      const Ctor = window.AudioContext || (window as unknown as { webkitAudioContext: typeof AudioContext }).webkitAudioContext;
      this.ctx = new Ctor();
    } catch {
      // No disponible; quedamos silenciosos
    }
  }

  setLevel(level: AlarmLevel): void {
    if (level === this.currentLevel) return;
    this.currentLevel = level;
    this.stopLoop();
    if (level !== 'off' && !this.isMuted()) {
      this.startLoop();
    }
  }

  /** Silencia alarma por N segundos (botón "silenciar 60s" estándar de monitor). */
  muteFor(seconds: number): void {
    this.muteUntil = Date.now() + seconds * 1000;
    this.muted = true;
    this.stopLoop();
    window.setTimeout(() => {
      this.muted = false;
      // Reactivar si todavía hay alarma activa
      if (this.currentLevel !== 'off') this.startLoop();
    }, seconds * 1000);
  }

  isMuted(): boolean {
    return this.muted && Date.now() < this.muteUntil;
  }

  private startLoop(): void {
    const beep = () => {
      if (!this.ctx || this.currentLevel === 'off' || this.isMuted()) return;
      if (this.currentLevel === 'critical') {
        this.triplet(1200, 0.08);
      } else {
        this.tone(800, 0.12);
      }
    };
    // Primer bip inmediato
    beep();
    const period = this.currentLevel === 'critical' ? 1500 : 1000;
    this.timer = window.setInterval(beep, period);
  }

  private stopLoop(): void {
    if (this.timer !== null) {
      window.clearInterval(this.timer);
      this.timer = null;
    }
  }

  private tone(freq: number, duration: number): void {
    if (!this.ctx) return;
    const osc = this.ctx.createOscillator();
    const gain = this.ctx.createGain();
    osc.type = 'sine';
    osc.frequency.setValueAtTime(freq, this.ctx.currentTime);
    gain.gain.setValueAtTime(0, this.ctx.currentTime);
    gain.gain.linearRampToValueAtTime(0.15, this.ctx.currentTime + 0.01);
    gain.gain.exponentialRampToValueAtTime(0.001, this.ctx.currentTime + duration);
    osc.connect(gain).connect(this.ctx.destination);
    osc.start();
    osc.stop(this.ctx.currentTime + duration + 0.05);
  }

  /** Triple bip rápido para alarma crítica. */
  private triplet(freq: number, duration: number): void {
    this.tone(freq, duration);
    window.setTimeout(() => this.tone(freq, duration), 130);
    window.setTimeout(() => this.tone(freq, duration), 260);
  }

  destroy(): void {
    this.stopLoop();
    this.ctx?.close().catch(() => { /* ignore */ });
    this.ctx = null;
  }
}

export const alarmSound = new AlarmSound();
